// Calendar events (20260917120000_calendar_events.sql).
//
// Reads go straight to the tables: RLS decides which events a person sees
// (company-wide, their own department, or everything at calendar:edit). Writes
// go through two RPCs only — the event, its department targets and the
// notifications to its audience have to land in one transaction, so the tables
// have no write policies at all.
//
// Kept out of the component so tests mock this module, not a PostgREST builder.
import { supabase } from './supabase';
import { addDaysToKey, orgDateKey } from './dates';
import type { EventType } from './orgSettings';

export type Audience = 'company' | 'admin' | 'department';

export type CalendarEvent = {
  id: string;
  title: string;
  /** An id from policy.calendar.types — resolve the label with eventTypeLabel. */
  type: string;
  all_day: boolean;
  /** All-day events: org-local dates, inclusive. Null for timed events. */
  start_date: string | null;
  end_date: string | null;
  /** Timed events: instants. Null for all-day events. */
  starts_at: string | null;
  ends_at: string | null;
  location: string | null;
  notes: string | null;
  audience: Audience;
  department_ids: string[];
  created_by: string | null;
  /** 'google': pulled from the company calendar — its title and time are Google's. */
  source: 'hyprflow' | 'google';
};

/** Just when an event happens — all eventSpan / whenText need. */
export type EventTimes = Pick<
  CalendarEvent,
  'all_day' | 'start_date' | 'end_date' | 'starts_at' | 'ends_at'
>;

export type EventInput = {
  /** null creates a new event. */
  id: string | null;
  title: string;
  type: string;
  all_day: boolean;
  /** 'YYYY-MM-DD', used when all_day. */
  start_date: string | null;
  end_date: string | null;
  /** 'YYYY-MM-DDTHH:mm' on the ORG's clock, used when not all_day. The database
   *  turns it into an instant with org_settings.tz_iana — the browser never does. */
  starts_local: string | null;
  ends_local: string | null;
  location: string;
  notes: string;
  audience: Audience;
  /** Only read when audience is 'department'. */
  department_ids: string[];
};

type DbError = { message: string } | null;

const SELECT =
  'id, title, type, all_day, start_date, end_date, starts_at, ends_at, location, notes, audience, created_by, source, calendar_event_departments(department_id)';

type Row = Omit<CalendarEvent, 'department_ids'> & {
  calendar_event_departments: { department_id: string }[] | null;
};

/** First and last org-local day an event touches. A timed event ending exactly
 *  at midnight does not spill onto the next day. */
export const eventSpan = (e: EventTimes, tz: string): [string, string] => {
  if (e.all_day) return [e.start_date ?? '', e.end_date ?? ''];
  const start = new Date(e.starts_at ?? 0).getTime();
  const end = new Date(e.ends_at ?? 0).getTime();
  return [orgDateKey(start, tz), orgDateKey(end > start ? end - 1 : end, tz)];
};

/** Every org-local day key an event covers, for placing it on the month grid. */
export const eventDays = (e: CalendarEvent, tz: string): string[] => {
  const [from, to] = eventSpan(e, tz);
  const days: string[] = [];
  // ponytail: capped at a year so a malformed span can't spin; events are days, not years.
  for (let d = from; d && d <= to && days.length < 366; d = addDaysToKey(d, 1)) days.push(d);
  return days;
};

/** Events overlapping the org-local days from..to (inclusive), soonest first.
 *  Cancelled events are left out.
 *
 *  `error` is the failed read. It used to be dropped (`data ?? []`), so a network
 *  or RLS failure drew an empty month and "Nothing in the next six months"
 *  (WS-V1). No backend at all is not a failure: the shell renders without one. */
export async function listEvents(
  from: string,
  to: string,
  tz: string,
): Promise<{ data: CalendarEvent[]; error: DbError }> {
  if (!supabase) return { data: [], error: null };
  // Timed events are instants: over-fetch a day either side, then keep the ones
  // whose org-local days overlap (docs/UI-CONVENTIONS.md §2).
  const { data, error } = await supabase
    .from('calendar_events')
    .select(SELECT)
    .is('cancelled_at', null)
    .or(
      `and(all_day.is.true,start_date.lte.${to},end_date.gte.${from}),` +
        `and(all_day.is.false,starts_at.lt.${addDaysToKey(to, 2)},ends_at.gte.${addDaysToKey(from, -1)})`,
    );
  const events = ((data as unknown as Row[] | null) ?? [])
    .map(({ calendar_event_departments, ...e }) => ({
      ...e,
      department_ids: (calendar_event_departments ?? []).map((d) => d.department_id),
    }))
    .filter((e) => {
      const [s, end] = eventSpan(e, tz);
      return s <= to && end >= from;
    })
    .sort(
      (a, b) =>
        eventSpan(a, tz)[0].localeCompare(eventSpan(b, tz)[0]) ||
        Number(b.all_day) - Number(a.all_day) ||
        (a.starts_at ?? '').localeCompare(b.starts_at ?? ''),
    );
  return { data: events, error };
}

/** Create (id null) or update an event. Returns the event id. */
export async function saveEvent(input: EventInput): Promise<{ id: string | null; error: DbError }> {
  if (!supabase) return { id: null, error: { message: 'Not connected.' } };
  const { data, error } = await supabase.rpc('save_calendar_event', {
    p_id: input.id,
    p_title: input.title,
    p_type: input.type,
    p_all_day: input.all_day,
    p_start_date: input.all_day ? input.start_date : null,
    p_end_date: input.all_day ? input.end_date : null,
    p_starts_local: input.all_day ? null : input.starts_local,
    p_ends_local: input.all_day ? null : input.ends_local,
    p_location: input.location,
    p_notes: input.notes,
    p_audience: input.audience,
    p_department_ids: input.audience === 'department' ? input.department_ids : [],
  });
  return { id: (data as string | null) ?? null, error };
}

/** Delete an event; its audience is told it was cancelled. */
export async function deleteEvent(id: string): Promise<{ error: DbError }> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const { error } = await supabase.rpc('delete_calendar_event', { p_id: id });
  return { error };
}

/** The live label for a type id; rename applies everywhere, and an id no longer
 *  in the list still renders as itself rather than vanishing. */
export const eventTypeLabel = (types: readonly EventType[], id: string): string =>
  types.find((t) => t.id === id)?.label ?? id;

/** Types the form offers: never an archived one, except the type the event being
 *  edited is already filed under (so saving it doesn't silently change it). */
export const pickableTypes = (types: readonly EventType[], current?: string): EventType[] =>
  types.filter((t) => !t.archived || t.id === current);
